import { z } from 'zod';
import { OPEN_WORLD_CONTENT_KINDS, type OpenWorldContentKind } from './content';
import type { OpenWorldDirection } from './direction';
import {
  isOpenWorldCoordinateWithin,
  OPEN_WORLD_ACTIVE_BOUNDS,
  type OpenWorldCoordinate,
  openWorldCoordinate,
  openWorldCoordinateKey,
} from './grid';

/**
 * The eight safe ports.
 *
 * ## Fixed, not generated
 *
 * They are written down here rather than drawn from the seed, and that is the
 * point: a port is the one place in the world whose location a player may rely
 * on before ever seeing it, and re-rolling the content version must never move
 * one. The generator reads this table and skips those quadrants entirely.
 *
 * ## Two per edge, and each one paired
 *
 * Every port carries the edge it sits on and the port it balances against - the
 * mirrored one on the opposite edge. Four pairs, eight ports, and no edge with
 * an advantage. The pair is recorded rather than computed so a future world
 * with different bounds can declare its own pairing without a rule to reverse.
 *
 * ## What a port is NOT
 *
 * Zero danger, zero resources, and none of the five content kinds: no mob, no
 * boss, no comet, no planet, no water. {@link OPEN_WORLD_SAFE_PORT_PROFILE}
 * states it as data so a generator cannot merely remember to skip them.
 *
 * NPCs, repair, docking and protection are not here. A port is a place with
 * nothing dangerous in it; what a player may DO there is a later gate.
 *
 * ## Bounds
 *
 * This table belongs to the active 10x10 world. A larger world would declare
 * its own table against its own bounds - which is why the coordinates are built
 * through {@link openWorldCoordinate} and asserted at module load rather than
 * trusted.
 */

/** Stable ids: which edge, and where along it. Never a letter, never an index. */
export const OPEN_WORLD_SAFE_PORT_IDS = [
  'north_west',
  'north_east',
  'south_west',
  'south_east',
  'west_north',
  'west_south',
  'east_north',
  'east_south',
] as const;

export type OpenWorldSafePortId = (typeof OPEN_WORLD_SAFE_PORT_IDS)[number];

/** The four balancing pairs. Each names exactly two ports on opposite edges. */
export const OPEN_WORLD_SAFE_PORT_PAIR_IDS = [
  /** `A/3` and `J/3` - the western meridian, north and south. */
  'meridian_west',
  /** `A/8` and `J/8` - the eastern meridian. */
  'meridian_east',
  /** `C/1` and `C/10` - the northern parallel, west and east. */
  'parallel_north',
  /** `H/1` and `H/10` - the southern parallel. */
  'parallel_south',
] as const;

export type OpenWorldSafePortPairId = (typeof OPEN_WORLD_SAFE_PORT_PAIR_IDS)[number];

export interface OpenWorldSafePort {
  readonly id: OpenWorldSafePortId;
  readonly coordinate: OpenWorldCoordinate;
  /** The edge of the world this port sits on. */
  readonly edge: OpenWorldDirection;
  /** The pair it belongs to. */
  readonly pairId: OpenWorldSafePortPairId;
  /** The other port in that pair. Derived once, here, and never recomputed. */
  readonly partnerId: OpenWorldSafePortId;
}

function port(
  id: OpenWorldSafePortId,
  label: readonly [rowIndex: number, columnIndex: number],
  edge: OpenWorldDirection,
  pairId: OpenWorldSafePortPairId,
  partnerId: OpenWorldSafePortId,
): OpenWorldSafePort {
  return Object.freeze({
    id,
    coordinate: openWorldCoordinate(label[0], label[1], OPEN_WORLD_ACTIVE_BOUNDS),
    edge,
    pairId,
    partnerId,
  });
}

/**
 * The eight, in a fixed order.
 *
 * Row indices: `A` is 0, `C` is 2, `H` is 7, `J` is 9. Column indices are one
 * less than the printed column: column `1` is 0, `3` is 2, `8` is 7, `10` is 9.
 */
export const OPEN_WORLD_SAFE_PORTS: readonly OpenWorldSafePort[] = Object.freeze([
  /** `A/3` */
  port('north_west', [0, 2], 'north', 'meridian_west', 'south_west'),
  /** `A/8` */
  port('north_east', [0, 7], 'north', 'meridian_east', 'south_east'),
  /** `J/3` */
  port('south_west', [9, 2], 'south', 'meridian_west', 'north_west'),
  /** `J/8` */
  port('south_east', [9, 7], 'south', 'meridian_east', 'north_east'),
  /** `C/1` */
  port('west_north', [2, 0], 'west', 'parallel_north', 'east_north'),
  /** `H/1` */
  port('west_south', [7, 0], 'west', 'parallel_south', 'east_south'),
  /** `C/10` */
  port('east_north', [2, 9], 'east', 'parallel_north', 'west_north'),
  /** `H/10` */
  port('east_south', [7, 9], 'east', 'parallel_south', 'west_south'),
]);

const PORTS_BY_COORDINATE_KEY: ReadonlyMap<string, OpenWorldSafePort> = new Map(
  OPEN_WORLD_SAFE_PORTS.map((entry) => [openWorldCoordinateKey(entry.coordinate), entry]),
);

/**
 * Every port sits inside the active world, and no two share a quadrant.
 *
 * Checked at module load rather than in a test: a typo here would place a
 * safe harbour in a quadrant that does not exist, and the failure would
 * otherwise surface as a missing port hundreds of lines away.
 */
for (const entry of OPEN_WORLD_SAFE_PORTS) {
  if (!isOpenWorldCoordinateWithin(entry.coordinate, OPEN_WORLD_ACTIVE_BOUNDS)) {
    throw new RangeError(`safe port ${entry.id} is outside the active world`);
  }
}
if (PORTS_BY_COORDINATE_KEY.size !== OPEN_WORLD_SAFE_PORTS.length) {
  throw new RangeError('two safe ports share a quadrant');
}

export function openWorldSafePortAt(coordinate: OpenWorldCoordinate): OpenWorldSafePort | null {
  return PORTS_BY_COORDINATE_KEY.get(openWorldCoordinateKey(coordinate)) ?? null;
}

export function isOpenWorldSafePort(coordinate: OpenWorldCoordinate): boolean {
  return PORTS_BY_COORDINATE_KEY.has(openWorldCoordinateKey(coordinate));
}

/**
 * What a safe port has instead of content: nothing, stated exhaustively.
 *
 * Built from {@link OPEN_WORLD_CONTENT_KINDS} rather than written out, so a
 * sixth kind added tomorrow is zero at a port without anyone remembering to
 * come back here.
 */
export const OPEN_WORLD_SAFE_PORT_PROFILE: Readonly<{
  readonly danger: 0;
  readonly resources: 0;
  readonly spaces: Readonly<Record<OpenWorldContentKind, 0>>;
}> = Object.freeze({
  danger: 0,
  resources: 0,
  spaces: Object.freeze(
    Object.fromEntries(OPEN_WORLD_CONTENT_KINDS.map((kind) => [kind, 0])) as Record<
      OpenWorldContentKind,
      0
    >,
  ),
});

/* -------------------------------------------------------------------------- */
/*  Wire                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * A port as a client may know it: where it is, which edge, which pair.
 *
 * Public in full, and safe to be: a port holds no secret. There is no field
 * here that a later gate could use to smuggle a docking right, a repair cost or
 * an NPC - those are not decided.
 */
export const openWorldSafePortSchema = z
  .object({
    id: z.enum(OPEN_WORLD_SAFE_PORT_IDS),
    coordinate: z
      .object({
        rowIndex: z.number().int().min(0),
        columnIndex: z.number().int().min(0),
      })
      .strict(),
    edge: z.enum(['north', 'south', 'east', 'west']),
    pairId: z.enum(OPEN_WORLD_SAFE_PORT_PAIR_IDS),
    partnerId: z.enum(OPEN_WORLD_SAFE_PORT_IDS),
  })
  .strict();
